import React, { useMemo, useEffect, useCallback, useReducer, useState } from 'react'
import { Grid, Typography, Paper } from '@material-ui/core'
import { Alert as ReferaAlert, Dialog, Loader } from '@refera/ui-web'
import { Danger as DangerIcon } from '@refera/ui-icons'
import { useSelector, useDispatch } from 'react-redux'
import { useParams, navigate } from '@reach/router'
import { AlertTitle, Alert } from '@mui/material'
import WarningIcon from '@mui/icons-material/Warning'

import Loading from '_components/loading'
import Button from '_components/button'
import Snackbar from '_components/snackbar'
import { ActionDeadline } from '_components/service-order'
import PaperInformation from '_views/service-order/paper-information'
import Actions from '_views/service-order/actions'
import Details from '_views/service-order/details'
import { getCategoriesLoadingSelector } from '_modules/categories/selectors'
import ShowMoreDetailRefuseModal from '_components/modal/show-more-refuse-detail'
import {
  getBudget,
  updateBudget,
  addBudgetPicture,
  UPDATE_BUDGET,
  DELETE_BUDGET_ITEM,
  UPDATE_BUDGET_ITEM,
  ADD_BUDGET_PICTURE,
  DELETE_BUDGET_PICTURE,
  REFUSE_BUDGET,
  UNREFUSE_BUDGET,
  SENT_BUDGET_COUNTER_PROPOSAL,
  APPROVE_BUDGET,
  CANCEL_APPROVE_BUDGET,
  deleteBudgetPictures,
  verifyPaidInstallment,
  getReasonsToRefusal,
} from '_modules/budget/actions'
import {
  getBudgetByIdSelector,
  getBudgetLoadingSelector,
  updateBudgetErrorSelector,
  getReasonsToRefuseSelector,
} from '_modules/budget/selectors'
import { TRIGGER_SNACKBAR } from '_modules/utils/actions'
import { getHistoryLogs } from '_modules/history-logs/actions'
import { getServiceOrder } from '_modules/service-orders/actions'
import useIsGenericParamActive from '_hooks/use-is-generic-param-active'
import useFetchCall from '_hooks/use-fetch-call'
import useRolePermission from '_hooks/use-role-permission'
import useToggle from '_hooks/use-toggle'
import { phoneNumberMask, formatDate } from '_utils/helpers'
import { currentServiceOrderSelector } from '_modules/service-orders/selectors'
import {
  TAX_REGIMES,
  SERVICE_ORDER_STATUS,
  FINANCE_PAYLOAD_KEYS_TO_UPDATE_BUDGET,
} from '_utils/constants'
import { GENERIC_PARAMETERS } from '_utils/constants/service-order'
import SwitchComponent from '_/components/switch'

import {
  INITIAL_STATE,
  UPDATE_STATE,
  UPDATE_BUDGET_ITEMS,
  ADD_BUDGET_ITEM,
  REMOVE_BUDGET_ITEM,
  REMOVE_BUDGET_PICTURES,
  ADD_BUDGET_PICTURES,
  reducer,
} from './reducer'
import useStyles from './styles'
import ApproveBudgetDrawer from './approve-budget-drawer'
import UnavailableActionBudgetModal from './unavailable-budget-dialog'
import CancelEditBudgetDialog from './cancel-edit-dialog'
import BudgetButtons from './buttons'
import Textfield from '_/components/textfield'
import { useToast } from '_/hooks/use-toast'
import { InfoServiceOrderStepStatusModal } from '../../help-step-status-modal'
import { getAdditionalTakeRatesByAgency } from '_/modules/agency/actions'
import { additionalTakeRatesSelector } from '_/modules/agency/selectors'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'

const notAllowedToEditBudgetText =
  'Como este orçamento foi aprovado, apenas os dados do responsável pelo pagamento poderão ser editados.'

const notAllowedToEditFileBudget =
  'Não foi possível realizar o upload dos arquivos, pois a extensão de pelo menos um deles não é aceita pelo campo de anexo em questão.'

const NOT_ALLOW_EDIT_BUDGET_REFUSED =
  'Como este orçamento foi recusado, nenhum dado pode ser editado. Favor clicar em Cancelar recusa se deseja editar este orçamento.'

const formatNumber = value => (value ? Number(value) : 0)

const EstimationStep = ({ handleShowHelpRequest, onEditChange }) => {
  const serviceOrder = useSelector(currentServiceOrderSelector)
  const styles = useStyles({ step: serviceOrder?.orderStep })
  const dispatch = useDispatch()
  const { budgetId, serviceOrderId } = useParams()
  const { isAdmin, isSAAS, checkIfUserDoesNotPermission } = useRolePermission()

  const isServicesLoading = useSelector(getCategoriesLoadingSelector)
  const isBudgetLoading = useSelector(getBudgetLoadingSelector)
  const budget = useSelector(getBudgetByIdSelector(budgetId))?.toJS()
  const updateBudgetError = useSelector(updateBudgetErrorSelector)
  const additionalTakeRates = useSelector(additionalTakeRatesSelector)
  const reasonsToRefuseList = useSelector(getReasonsToRefuseSelector('external_budget'))

  const initialModal = useMemo(() => {
    return { isOpen: false, message: '', icon: '', type: 'info', action: () => {} }
  }, [])
  const [modal, setModal] = useState(initialModal)

  const [state, localDispatch] = useReducer(reducer, INITIAL_STATE)
  // const [isEdit, onToggleEdit] = useToggle(false)
  const [isEdit, setIsEdit] = useState(false)
  const [isApproveBudgetDrawerOpen, onToggleApproveBudgetDrawerOpen] = useToggle()
  const [isNotAllowedToEditBudgetText, onToggleNotAllowedToEditBudgetText] = useToggle()
  const [isNotAllowedToEditFileBudget, onToggleNotAllowedToEditFileBudget] = useToggle()
  const [isCancelEditDialogOpen, onToggleCancelEditDialog] = useToggle()
  const [isBackToServiceOrder, onToggleBackToServiceOrder] = useToggle()
  const [isNotAllowToEditBudget, onToggleNotAllowToEditBudget] = useToggle()
  const [showDetailRefuse, toggleShowDatailRefuse] = useToggle()
  const [isExpanded, onToggleExpanded] = useToggle(false)
  const [isSavingBudget, setSavingBudget] = useState({
    updateBudget: false,
    deleteBudgetItem: false,
    updateBudgetItem: false,
    deleteBudgetPicture: false,
    addBudgetPicture: false,
  })
  const agencyContractType = useMemo(() => serviceOrder?.agency.get('contractType'), [serviceOrder])

  const [updatingInstallments, setUpdatingInstallments] = useState(false)
  const { showToast } = useToast()
  const isSimplified = budget?.isSimplified

  const spreadTaxesOnBudgetItems = useMemo(() => {
    // Verifica se o usuário tem permissão para ver as taxas da refera e intermediária
    // para dissolvê-las nos itens do orçamento caso não tenha
    return checkIfUserDoesNotPermission(
      PERMISSIONS.TAX_RATES,
      PERMISSIONS_ACTIONS.READ,
      undefined,
      false
    )
  }, [checkIfUserDoesNotPermission])

  const alert = useMemo(() => {
    if (isSimplified) {
      return {
        isOpen: true,
        title:
          'Este é um orçamento no modelo simplificado (o prestador envia um PDF). Nele, você pode editar apenas os valores. Para visualizar o orçamento enviado pelo prestador, faça o download no botão de "baixar" ➜ ao lado do botão "Editar".',
        severity: 'info',
      }
    }
    const regimes = [budget?.budgetPayer?.[0]?.regime, budget?.tradesman?.companyTaxRegime]

    const hasRegimeWithoutTax = regime => TAX_REGIMES.WITHOUT_TAX.includes(regime)

    if (
      !regimes.some(hasRegimeWithoutTax) &&
      budget?.withholdingTax <= 0 &&
      serviceOrder?.stage === SERVICE_ORDER_STATUS.FINISHED
    ) {
      return {
        isOpen: true,
        title: 'O prestador não inseriu o valor do imposto retido',
        severity: 'warning',
      }
    }

    return { isOpen: false, title: '', severity: '' }
  }, [
    budget?.budgetPayer?.[0]?.regime,
    budget?.tradesman?.companyTaxRegime,
    budget?.withholdingTax,
    serviceOrder?.stage,
  ])

  const reasonOptionsMap = useMemo(
    () =>
      reasonsToRefuseList?.reduce((acc, option) => {
        acc[option.slug] = option.reason
        return acc
      }, {}) || {},
    [reasonsToRefuseList]
  )

  const reasonForRefusal = useMemo(
    () => (budget?.reasonForRefusal ? reasonOptionsMap[budget.reasonForRefusal] : ''),
    [budget?.reasonForRefusal, reasonOptionsMap]
  )

  const isSavingLoading = useMemo(
    () => Object.values(isSavingBudget).some(value => value === true),
    [isSavingBudget]
  )

  const isOriginal = useMemo(() => budget?.isOriginal, [budget?.isOriginal])
  const agency = useMemo(() => serviceOrder?.get('agency')?.toJS(), [serviceOrder])
  const budgetPayer = useMemo(() => budget?.budgetPayer?.[0], [budget?.budgetPayer])
  const isAlertShowing = useMemo(() => Boolean(budget?.refused), [budget?.refused])

  const handleGetIsActive = useCallback(() => {
    dispatch(getServiceOrder(serviceOrderId))
  }, [dispatch, serviceOrderId])

  const isAbleToEdit = isAdmin || isSAAS

  const onSetInitialBudgetState = useCallback(
    dataValue => {
      const budgetObj = dataValue || budget

      // Se o orçamento tiver taxas dissolvidas nos itens,
      // o valor do prestador de serviço é o total do orçamento menos o valor do imposto retido
      const totalPriceTradesman = spreadTaxesOnBudgetItems
        ? budgetObj?.priceTotal - budgetObj?.financialIncomeValue
        : budgetObj.totalPriceTradesman

      const payload = {
        diagnosis: budgetObj.diagnosis || '',
        proposedSolution: budgetObj.proposedSolution || '',
        budgetItems: budgetObj.budgetItems || [],
        acceptsInstallment: budgetObj.acceptsInstallment || false,
        installments: budgetObj.installments || '',
        acceptsWarranty: budgetObj.acceptsWarranty || false,
        warrantyMonths: budgetObj.warrantyMonths || '',
        visitHappened: budgetObj.visitHappened || false,
        totalPriceTradesman: totalPriceTradesman || '',
        totalPriceServiceProvider: budgetObj.totalPriceServiceProvider || 0,
        totalPrice: budgetObj.priceTotal || 0,
        liquidValue: budgetObj.liquidValue || 0,
        referaCompletionPercentage: budgetObj?.referaCompletionPercentage || 0,
        referaCompletionPercentageValue: budgetObj?.priceRefera || 0,
        agencyCompletionPercentage: budgetObj?.agencyCompletionPercentage || 0,
        agencyCompletionPercentageValue: budgetObj?.priceAgency || 0,
        withholdingTax: budgetObj?.withholdingTax || 0,
        withholdingTaxValue: budgetObj?.withholdingTaxValue || 0,
        financialIncome: budgetObj?.financialIncome || 0,
        originalFinancialIncome: budgetObj?.financialIncome || 0,
        maxTaxAllowed: budgetObj.maxTaxAllowed || 0,
        financialIncomeValue: budgetObj?.financialIncomeValue || 0,
        budgetPictures: budgetObj.budgetPictures || [],
        personPaying: budgetObj.personPaying,
        personPayingEmail: budgetObj.personPayingEmail || '',
        personPayingWhatsapp: budgetObj.personPayingWhatsapp || undefined,
        createdAt: budgetObj.createdAt || '',
        blockFinanceTransfers: budgetObj?.blockFinanceTransfers || false,
        additionalTakeRate: budgetObj?.additionalTakeRate || 0,
        additionalTakeRatePerc: budgetObj?.additionalTakeRatePerc || 0,
        priceAgency: budgetObj?.priceAgency || 0,
        priceRefera: budgetObj?.priceRefera || 0,
        warrantyValue: budgetObj?.warrantyValue || 0,
        referaRegularTax: budgetObj?.referaRegularTax || 0,
        referaRegularTaxPercentage: budgetObj?.referaRegularTaxPercentage || 0,
        revenueShare: budgetObj?.revenueShare || 0,
        agencyAdditionalTakeRates: additionalTakeRates,
        additionalTakeRateChange: false,
        isSimplified: budgetObj?.isSimplified || false,
        extendedWarranty: budgetObj?.extendedWarranty || false,
        extendedWarrantyValue: budgetObj?.extendedWarrantyValue || 0,
        additionalWarrantyMonths: budgetObj?.additionalWarrantyMonths || '',
      }

      localDispatch({
        type: UPDATE_STATE,
        payload,
      })
    },
    [budget, additionalTakeRates, spreadTaxesOnBudgetItems]
  )

  const showSuccessToast = () => showToast({ type: 'success' })

  useFetchCall(REFUSE_BUDGET.ACTION, showSuccessToast)

  const onUpdateBudgetSuccessful = useCallback(() => {
    setSavingBudget(prevState => ({
      ...prevState,
      updateBudget: false,
      deleteBudgetItem: false,
      updateBudgetItem: false,
      deleteBudgetPicture: false,
      addBudgetPicture: false,
    }))

    localDispatch({
      type: UPDATE_STATE,
      payload: { additionalTakeRateChange: false },
    })

    handleGetIsActive()
  }, [handleGetIsActive])

  const onDeleteBudgetItemsSuccessful = useCallback(() => {
    let payload = {
      budgetItems: budget?.budgetItems,
    }
    if (state.budgetItemsToRemove) {
      payload = {
        ...payload,
        budgetItemsToRemove: [],
      }
    }

    setSavingBudget(prevState => ({
      ...prevState,
      deleteBudgetItem: false,
    }))

    localDispatch({
      type: UPDATE_STATE,
      payload,
    })
  }, [budget?.budgetItems, state.budgetItemsToRemove])

  const onDeleteBudgetItemsRejected = useCallback(() => {
    setSavingBudget(prevState => ({
      ...prevState,
      deleteBudgetItem: false,
    }))
  }, [])

  useFetchCall(
    DELETE_BUDGET_ITEM.ACTION,
    onDeleteBudgetItemsSuccessful,
    onDeleteBudgetItemsRejected
  )

  const onEditBudgetItemsSuccessful = useCallback(() => {
    let payload = {
      budgetItems: budget?.budgetItems,
    }
    if (state.budgetItemsToEdit) {
      payload = {
        ...payload,
        budgetItemsToEdit: [],
      }
    }

    setSavingBudget(prevState => ({
      ...prevState,
      updateBudgetItem: false,
    }))

    localDispatch({
      type: UPDATE_STATE,
      payload,
    })
  }, [budget?.budgetItems, state.budgetItemsToEdit])

  const onEditBudgetItemsRejected = useCallback(() => {
    setSavingBudget(prevState => ({
      ...prevState,
      updateBudgetItem: false,
    }))
  }, [])

  useFetchCall(UPDATE_BUDGET_ITEM.ACTION, onEditBudgetItemsSuccessful, onEditBudgetItemsRejected)

  const onAddBudgetPictureSuccessful = useCallback(() => {
    setSavingBudget(prevState => ({
      ...prevState,
      addBudgetPicture: false,
    }))

    localDispatch({
      type: UPDATE_STATE,
      payload: { budgetPicturesToAdd: [] },
    })
  }, [state])

  const onDeleteBudgetPictureSuccessful = useCallback(() => {
    setSavingBudget(prevState => ({
      ...prevState,
      deleteBudgetPicture: false,
    }))

    localDispatch({
      type: UPDATE_STATE,
      payload: { budgetPicturesToRemove: [] },
    })
  }, [state])

  const onBackToServiceOrderClick = useCallback(() => {
    onToggleCancelEditDialog()

    if (!isBackToServiceOrder) {
      onToggleBackToServiceOrder()
    }
  }, [isBackToServiceOrder, onToggleBackToServiceOrder, onToggleCancelEditDialog])

  const onCloseCancelEditBudgetDialog = useCallback(() => {
    onToggleCancelEditDialog()

    if (isBackToServiceOrder) {
      onToggleBackToServiceOrder()
    }
  }, [isBackToServiceOrder, onToggleBackToServiceOrder, onToggleCancelEditDialog])

  // TODO: Remove this dispatch once the BE is sending the step_status info when updating the budget
  const refetchServiceOrder = useCallback(() => {
    dispatch(getServiceOrder(serviceOrderId))
  }, [dispatch, serviceOrderId])

  const renderPapersPayerData = useMemo(
    () => [
      [
        {
          id: 1,
          label: 'Dados do prestador',
          value: [
            {
              value: `
                  ${budget?.tradesman?.companyName || ''}
                  ${`, ${phoneNumberMask(budget?.tradesman?.phone)}` || ''}
                  `,
              name: 'providerData',
            },
          ],
        },
      ],
      [
        {
          id: 2,
          label: 'Data do recebimento do orçamento',
          value: [
            {
              value: formatDate(budget?.createdAt),
              name: 'budgetCreatedAt',
            },
          ],
        },
      ],
    ],
    [budget, state.diagnosis, state.proposedSolution]
  )
  const renderPapersSolutionsData = useMemo(
    () => [
      [
        {
          id: 3,
          label: 'Diagnóstico do problema',
          value: [{ value: state.diagnosis, name: 'diagnosis' }],
        },
      ],
      [
        {
          id: 4,
          label: 'Solução do problema',
          value: [{ value: state.proposedSolution, name: 'proposedSolution' }],
        },
      ],
    ],
    [budget, state.diagnosis, state.proposedSolution]
  )

  const updatedStateAndLog = useCallback(() => {
    dispatch(getHistoryLogs(serviceOrderId))
  }, [dispatch, serviceOrderId])

  const handleCloseModal = useCallback(() => {
    setModal(old => ({ ...old, isOpen: false }))
  }, [setModal, initialModal])

  const handleVerifyPaidInstallment = async () => {
    let cannotEdit = false

    await Promise.resolve(dispatch(verifyPaidInstallment(serviceOrderId, budgetId))).catch(err => {
      setModal({
        isOpen: true,
        message: err || 'Ocorreu um erro ao verificar o parcelamento.',
        icon: DangerIcon,
        type: 'info',
        action: handleCloseModal,
      })
      cannotEdit = true
    })
    return cannotEdit
  }

  const onEditBudgetClick = useCallback(async () => {
    if (budget.refused) {
      onToggleNotAllowToEditBudget()
      return
    }
    if (!isEdit && budget?.approved && !isAdmin) {
      onToggleNotAllowedToEditBudgetText()
    }
    if (budgetPayer && (await handleVerifyPaidInstallment())) {
      return
    }
    setIsEdit(true)
    onEditChange(!isEdit)
  }, [
    isEdit,
    setModal,
    budgetPayer,
    onEditChange,
    budget?.refused,
    budget?.approved,
    handleVerifyPaidInstallment,
    onToggleNotAllowToEditBudget,
    onToggleNotAllowedToEditBudgetText,
  ])

  const onFieldChange = useCallback(event => {
    const { name, value } = event.target

    localDispatch({
      type: UPDATE_STATE,
      payload: { [name]: value },
    })
  }, [])

  const onRadioChange = useCallback(event => {
    const { name, value } = event.target

    localDispatch({
      type: UPDATE_STATE,
      payload: { [name]: value === 'true' },
    })
  }, [])

  const onSwitchChange = useCallback(event => {
    const { name, checked } = event.target

    localDispatch({
      type: UPDATE_STATE,
      payload: { [name]: checked },
    })
  }, [])

  const handleBudgetItemsChange = useCallback(event => {
    const { name, value, dataset } = event.target
    const elementIndex = Number(dataset.id)

    localDispatch({
      type: UPDATE_BUDGET_ITEMS,
      elementIndex,
      payload: { [name]: value },
    })
  }, [])

  const onAddBudgetItem = useCallback(() => {
    localDispatch({
      type: ADD_BUDGET_ITEM,
    })
  }, [])

  const onRemoveBudgetItem = useCallback(itemToDelete => {
    localDispatch({
      type: REMOVE_BUDGET_ITEM,
      payload: { itemIndex: itemToDelete?.index, itemId: itemToDelete?.id },
    })
  }, [])

  const onAddPicture = useCallback(picture => {
    localDispatch({
      type: ADD_BUDGET_PICTURES,
      payload: { picture },
    })
  }, [])

  const onRemovePicture = useCallback(attachmentIndex => {
    localDispatch({
      type: REMOVE_BUDGET_PICTURES,
      payload: {
        attachmentIndex,
      },
    })
  }, [])

  const onCancelEditClick = useCallback(() => {
    dispatch(getBudget(serviceOrderId, budgetId)).then(refetchServiceOrder)
    onSetInitialBudgetState()
    setIsEdit(false)
    onEditChange(!isEdit)
    onToggleCancelEditDialog()

    if (isBackToServiceOrder) {
      onToggleBackToServiceOrder()
      navigate(`/chamado/${serviceOrderId}`)
    }
  }, [
    isBackToServiceOrder,
    onSetInitialBudgetState,
    onToggleBackToServiceOrder,
    onToggleCancelEditDialog,
    onEditChange,
    serviceOrderId,
  ])

  const verifyAndFormatFinanceItensPayload = (payload, stateToFormat = state) => {
    const formattedPayload = { ...payload }

    Object.keys(formattedPayload)?.forEach(payloadItem => {
      if (FINANCE_PAYLOAD_KEYS_TO_UPDATE_BUDGET.includes(payloadItem)) {
        formattedPayload[payloadItem] = formatNumber(stateToFormat[payloadItem])
      }
    })

    return formattedPayload
  }

  const onSaveBudget = useCallback(
    async (overrides = {}) => {
      const stateToSave = {
        ...state,
        ...overrides,
      }
      let payload = {}
      let willDispatch = false

      Object.keys(stateToSave).map(key => {
        const keysToRemove = [
          'budgetItemsToRemove',
          'budgetItemsToEdit',
          'budgetItems',
          'budgetPicturesToRemove',
          'budgetPicturesToAdd',
          'budgetPictures',
          'originalFinancialIncome',
          'personPaying',
          'personPayingWhatsapp',
        ]

        const shouldAddOnPayload =
          (!keysToRemove.includes(key) &&
            stateToSave[key] !== budget[key] &&
            !(typeof budget[key] === 'undefined' && stateToSave[key] === '')) ||
          FINANCE_PAYLOAD_KEYS_TO_UPDATE_BUDGET.includes(key)

        if (shouldAddOnPayload) {
          if ((key === 'installments' || key === 'warrantyMonths') && stateToSave[key] === '') {
            payload[key] = 0
          } else {
            payload[key] = stateToSave[key]
          }
        }
        return null
      })

      if (stateToSave?.blockFinanceTransfers !== budget?.blockFinanceTransfers) {
        payload.blockFinanceTransfers = stateToSave.blockFinanceTransfers
      } else {
        delete payload?.blockFinanceTransfers
      }

      payload.budgetItems = stateToSave.budgetItems

      payload = verifyAndFormatFinanceItensPayload(payload, stateToSave)

      if (await handleVerifyPaidInstallment()) return

      if (Object.values(payload).length) {
        setSavingBudget(prevState => ({
          ...prevState,
          updateBudget: true,
        }))

        if (
          payload.agencyCompletionPercentage ||
          payload.referaCompletionPercentage ||
          payload.withholdingTax
        ) {
          setUpdatingInstallments(true)
        }

        willDispatch = true
        await dispatch(updateBudget(serviceOrderId, budgetId, payload))
          .then(() => {
            onUpdateBudgetSuccessful()
            refetchServiceOrder()
          })
          .catch(updateError => {
            console.warn('Error: ', updateError?.error_code)
            willDispatch = false
            showToast({
              type: 'error',
              message: updateError?.error_message || 'Ocorreu um erro ao realizar a operação',
            })
            setSavingBudget(prevState => ({
              ...prevState,
              updateBudget: false,
            }))
          })
      }

      if (state.budgetPicturesToRemove?.length) {
        setSavingBudget(prevState => ({
          ...prevState,
          deleteBudgetPicture: true,
        }))
        dispatch(
          deleteBudgetPictures(serviceOrderId, budgetId, {
            ids: state.budgetPicturesToRemove,
          })
        ).then(onDeleteBudgetPictureSuccessful)
      }

      if (state.budgetPicturesToAdd?.length) {
        setSavingBudget(prevState => ({
          ...prevState,
          addBudgetPicture: true,
        }))

        const picturePayload = {
          budgetPictures: state.budgetPicturesToAdd,
        }
        // eslint-disable-next-line consistent-return
        picturePayload?.budgetPictures.forEach(async picture => {
          await Promise.resolve(dispatch(addBudgetPicture(serviceOrderId, budgetId, picture))).then(
            onAddBudgetPictureSuccessful()
          )
        })
        willDispatch = true
      }

      if (willDispatch) {
        setIsEdit(false)
        onEditChange(!isEdit)
        updatedStateAndLog()
      }
    },
    [
      budget,
      budgetId,
      dispatch,
      serviceOrderId,
      state,
      onSetInitialBudgetState,
      onToggleNotAllowedToEditFileBudget,
      updatedStateAndLog,
      setSavingBudget,
    ]
  )

  const isDelayedActive = useIsGenericParamActive({
    name: GENERIC_PARAMETERS.DELAYED,
  })

  const fetchAdditionalTakeRates = useCallback(() => {
    if (!agency?.id) return
    // prevent duplicate requests
    if (additionalTakeRates?.length > 0 && additionalTakeRates?.[0]?.agency === agency?.id) return

    dispatch(getAdditionalTakeRatesByAgency(agency?.id))
  }, [agency, dispatch, additionalTakeRates, localDispatch])

  useEffect(() => {
    if (additionalTakeRates?.length > 0) {
      localDispatch({
        type: UPDATE_STATE,
        payload: { agencyAdditionalTakeRates: additionalTakeRates },
      })
    }
  }, [additionalTakeRates])

  useEffect(() => {
    dispatch(getBudget(serviceOrderId, budgetId)).then(dataResponse => {
      onSetInitialBudgetState(dataResponse)
      refetchServiceOrder()
    })
  }, [serviceOrderId, budgetId])

  useEffect(() => {
    fetchAdditionalTakeRates()
  }, [agency])

  useEffect(() => {
    dispatch(getReasonsToRefusal())
  }, [dispatch])

  useEffect(() => {
    const confirmExit = () => {
      if (isEdit) {
        return 'Salve antes as suas alterações no registro.'
      }

      return null
    }

    window.onbeforeunload = confirmExit
  }, [isEdit])

  if (isServicesLoading || isBudgetLoading || !budget || !Object.keys(budget).length) {
    return <Loading className={styles.loader} />
  }

  return (
    <Grid className={styles.container}>
      {(isSavingLoading || !budget) && <Loader hasBackdrop open={isSavingLoading || !budget} />}
      {alert.isOpen && (
        <ReferaAlert severity={alert.severity}>
          <AlertTitle>{alert.title}</AlertTitle>
        </ReferaAlert>
      )}
      {!budget?.refused && isDelayedActive && (
        <Grid item xs={12} className={styles.actionDeadlineWrapper}>
          <ActionDeadline handleShowHelpRequest={handleShowHelpRequest} />
          {agencyContractType === 'SAAS' && <InfoServiceOrderStepStatusModal />}
        </Grid>
      )}
      <Actions
        serviceOrderId={serviceOrder.id}
        onToggleEdit={onEditBudgetClick}
        isEdit={isEdit}
        onCancelEditClick={onToggleCancelEditDialog}
        onSaveClick={onSaveBudget}
        isOriginalBudget={isOriginal}
        onBackToServiceOrderClick={onBackToServiceOrderClick}
        isSavingBudget={isSavingLoading}
      />

      {isAdmin && budget?.tradesman && (
        <Grid className={styles.switchContainer}>
          <Typography>Bloquear repasses financeiros</Typography>
          <SwitchComponent
            label="Bloquear repasses financeiros"
            checked={!!state?.blockFinanceTransfers || false}
            name="blockFinanceTransfers"
            onChange={onSwitchChange}
            disabled={!isEdit}
          />
        </Grid>
      )}
      {isAlertShowing && (
        <>
          <Alert
            severity="warning"
            color="error"
            variant="standard"
            className={styles.alertBackground}
            icon={<WarningIcon fontSize="large" color="error" />}
            sx={{
              borderBottom: '4px groove red',
              borderEndEndRadius: 0,
              borderEndStartRadius: 0,
              marginBottom: '15px',
            }}
          >
            <AlertTitle className={styles.alertTitle}>Orçamento recusado</AlertTitle>
            <p style={{ color: '#696969', fontSize: '16px', fontFamily: 'Roboto' }}>
              O orçamento foi recusado pelo seguinte motivo: {reasonForRefusal}
            </p>
            <br />
            <Button
              style={{
                textDecoration: 'none',
                color: '#1671ED',
                border: 'none',
                background: 'none',
                padding: '0px',
              }}
              onClick={toggleShowDatailRefuse}
            >
              Ver mais detalhes
            </Button>
          </Alert>
        </>
      )}
      <Grid className={styles.mainInfo}>
        <Grid className={styles.paperWrapper}>
          {renderPapersPayerData.map(options => {
            if (budget?.proposeOpened && !budget?.tradesman && options[0]?.id === 1) {
              return (
                <Paper variant="outlined" className={styles.proposeOpened} key={options[0]?.id}>
                  <Typography>Orçamento interno</Typography>
                </Paper>
              )
            }
            return (
              <PaperInformation
                key={options[0].id}
                options={options}
                className={styles[`paper${options[0].id}`]}
                isEdit={options[0].id !== 1 && isEdit && isAbleToEdit}
                onChange={onFieldChange}
              />
            )
          })}
        </Grid>
        <Grid className={styles.content}>
          <Typography className={styles.cardTitle}>Descrição do chamado</Typography>
          <Grid className={styles.card}>
            <Textfield
              value={serviceOrder?.description}
              multiline
              InputProps={{
                disableUnderline: true,
                className: styles.textField,
              }}
              disabled
            />
          </Grid>
        </Grid>
        <Grid className={styles.paperWrapper}>
          {!isSimplified &&
            renderPapersSolutionsData.map(options => {
              return (
                <PaperInformation
                  key={options[0].id}
                  options={options}
                  className={styles[`paper${options[0].id}`]}
                  isEdit={options[0].id !== 1 && isEdit && isAbleToEdit}
                  onChange={onFieldChange}
                />
              )
            })}
        </Grid>

        <Grid className={styles.descriptionWrapper}>
          {!isSimplified && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h5" component="p" className={styles.descriptionTitle}>
                Orçamento detalhado
              </Typography>
              <Button
                variant="text"
                onClick={onToggleExpanded}
                style={{
                  textDecoration: 'none',
                  color: '#1671ED',
                  border: 'none',
                  background: 'none',
                  padding: '0px',
                  marginLeft: '15px',
                  marginBottom: '12px',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                {isExpanded ? 'Recolher' : 'Expandir'}
              </Button>
            </div>
          )}
          <Details
            isExpanded={isExpanded}
            serviceOrderId={serviceOrderId}
            budgetId={budgetId}
            orderDetails={serviceOrder?.orderDetails}
            budget={state}
            budgetData={budget}
            isOriginalBudget={isOriginal}
            isEdit={isEdit}
            handleInputChange={onFieldChange}
            onRadioChange={onRadioChange}
            handleBudgetItemsChange={handleBudgetItemsChange}
            onAddBudgetItem={onAddBudgetItem}
            onRemoveBudgetItem={onRemoveBudgetItem}
            isAbleToEdit={isAbleToEdit}
            errors={updateBudgetError}
            onAddPicture={onAddPicture}
            onRemovePicture={onRemovePicture}
            onSaveBudget={onSaveBudget}
          />
        </Grid>
      </Grid>
      <BudgetButtons
        isEdit={isEdit}
        setSavingBudget={setSavingBudget}
        isSavingLoading={isSavingLoading}
      />
      {isCancelEditDialogOpen && (
        <CancelEditBudgetDialog
          isOpen={isEdit}
          onClose={onCloseCancelEditBudgetDialog}
          onCancelEdit={onCancelEditClick}
        />
      )}
      <ApproveBudgetDrawer
        isOpen={isApproveBudgetDrawerOpen}
        onClose={onToggleApproveBudgetDrawerOpen}
        budget={budget}
        serviceOrderId={serviceOrderId}
        onToggleApproveBudgetDrawerOpen={onToggleApproveBudgetDrawerOpen}
        updatedStateAndLog={updatedStateAndLog}
      />
      {isNotAllowedToEditBudgetText && (
        <UnavailableActionBudgetModal
          title="Aprovar orçamento"
          text={notAllowedToEditBudgetText}
          handleModal={onToggleNotAllowedToEditBudgetText}
          handleConfirm={onToggleNotAllowedToEditBudgetText}
        />
      )}
      {isNotAllowedToEditFileBudget && (
        <UnavailableActionBudgetModal
          text={notAllowedToEditFileBudget}
          handleModal={onToggleNotAllowedToEditFileBudget}
          handleConfirm={onToggleNotAllowedToEditFileBudget}
        />
      )}
      {isNotAllowToEditBudget && (
        <UnavailableActionBudgetModal
          text={NOT_ALLOW_EDIT_BUDGET_REFUSED}
          handleModal={onToggleNotAllowToEditBudget}
          handleConfirm={onToggleNotAllowToEditBudget}
        />
      )}
      {showDetailRefuse && (
        <ShowMoreDetailRefuseModal
          handleModal={toggleShowDatailRefuse}
          refusalMoreDetails={budget?.refusalMoreDetails}
        />
      )}
      <Dialog
        open={modal?.isOpen}
        icon={modal?.icon}
        type={modal?.type}
        subject={modal?.message}
        onApprove={modal?.action}
      />
      <Snackbar
        action={[
          !updatingInstallments && UPDATE_BUDGET.ACTION,
          DELETE_BUDGET_ITEM.ACTION,
          UPDATE_BUDGET_ITEM.ACTION,
          ADD_BUDGET_PICTURE.ACTION,
          DELETE_BUDGET_PICTURE.ACTION,
          UNREFUSE_BUDGET.ACTION,
          SENT_BUDGET_COUNTER_PROPOSAL.ACTION,
          APPROVE_BUDGET.ACTION,
          TRIGGER_SNACKBAR.ACTION,
          CANCEL_APPROVE_BUDGET.ACTION,
        ]}
      />
    </Grid>
  )
}

export default React.memo(EstimationStep)
